"use client";

import { cn } from "@/lib/utils";
import { useAuth } from "@/providers/auth-provider";
import { reactToMessage } from "@/services/chat/messages";
import { useMutation } from "@tanstack/react-query";
import { ThumbsUpIcon } from "lucide-react";
import { useState } from "react";

const MessageReactionButton = ({
	messageId,
	messageType,
	isReacted: defaultIsReacted,
	totalReactions: defaultTotalReactions = 0,
}: {
	messageId: string;
	messageType: "sent" | "received";
	isReacted: boolean;
	totalReactions: number;
}) => {
	const { isLoggedIn } = useAuth();
	const [isReacted, setIsReacted] = useState<boolean>(defaultIsReacted);
	const [totalReactions, setTotalReactions] = useState<number>(defaultTotalReactions);

	const { mutate, isLoading } = useMutation({
		mutationFn: (messageId: string) => reactToMessage({ messageId }),
		onSuccess: (data) => {
			if (!data.success) {
				return;
			}

			if (data.type === "remove") {
				setTotalReactions(totalReactions - 1);
			} else if (data.type === "add") {
				setTotalReactions(totalReactions + 1);
			}

			setIsReacted((prev) => !prev);
		},
	});

	return (
		<button
			type="button"
			onClick={() => mutate(messageId)}
			disabled={isLoading || !isLoggedIn}
			className={cn(
				"absolute bottom-0 flex items-center justify-start disabled:cursor-not-allowed",
				messageType === "sent"
					? isReacted
						? "left-[-28px]"
						: "left-[-24px]"
					: isReacted
					? "right-[-28px] flex-row-reverse"
					: "right-[-24px] flex-row-reverse",
				isReacted ? "gap-0.5" : "gap-1.5"
			)}
		>
			{totalReactions > 0 && (
				<p
					className={cn(
						"ml-[-4px] text-xs",
						isReacted ? "font-semibold text-brand-primary" : "text-gray-400"
					)}
				>
					{totalReactions}
				</p>
			)}

			{isReacted || totalReactions > 0 ? (
				<span className="px-px text-lg">&#128077;</span>
			) : (
				<ThumbsUpIcon className="h-3.5 w-3.5 px-px text-gray-400" />
			)}
		</button>
	);
};

export default MessageReactionButton;
